import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ZoomOutOutlined from "@mui/icons-material/ZoomOutOutlined";
import ZoomInOutlined from "@mui/icons-material/ZoomInOutlined";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import Cropper from "react-easy-crop";
import FileDropzone from "../../../anexosCircuito/fileDropzone";
import {
  createCustomEquipmentType,
  updateCustomEquipmentType,
} from "../customEquipmentTypesService";
import { cropIconToCircle } from "../cropUtils";
import usePriorityPaste from "../../../usePriorityPaste";
import styles from "./styles";

const ACCEPT_ICON_TYPES = "image/png, image/jpeg, image/svg+xml, image/webp";
const MAX_ICON_SIZE_MB = 5;
const MAX_ICON_SIZE_BYTES = MAX_ICON_SIZE_MB * 1024 * 1024;
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;

const CROP_INICIAL = { x: 0, y: 0 };

// Converte um File em data URL, usado para alimentar o Cropper
const lerArquivoComoDataUrl = (arquivo) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader?.result?.toString?.() || "");
    reader.onerror = reject;
    reader?.readAsDataURL?.(arquivo);
  });

// Modal de criação/edição de um tipo de equipamento personalizado (título + ícone),
// aberto a partir da própria paleta do diagrama. O ícone passa por um recorte circular
// (react-easy-crop) antes do envio, para ficar consistente com a moldura já usada nos cards.
export default function ModalEquipamentoPersonalizado({
  open = false,
  onClose = () => {},
  onSaved = () => {},
  mode = "create",
  initialData = null,
  setSnackbar = () => {},
}) {
  const theme = useTheme();
  const modalStyles = styles(theme);

  const [titulo, setTitulo] = useState("");
  const [file, setFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState(CROP_INICIAL);
  const [zoom, setZoom] = useState(ZOOM_MIN);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  // Reseta o formulário sempre que o modal abre, considerando o modo (criar/editar);
  // em edição, já carrega o ícone atual no cropper para permitir reenquadrar sem reenviar
  useEffect(() => {
    if (!open) return;
    setTitulo(initialData?.descricao || "");
    setFile(null);
    setImageSrc(initialData?.iconUrl || null);
    setCrop(CROP_INICIAL);
    setZoom(ZOOM_MIN);
    setCroppedAreaPixels(null);
    setSaving(false);
  }, [open, initialData]);

  const validarEDefinirArquivo = async (selecionado) => {
    if (!selecionado) return;
    if (!selecionado?.type?.startsWith?.("image/")) {
      setSnackbar?.({
        children: "Selecione um arquivo de imagem válido.",
        severity: "error",
      });
      return;
    }
    if (selecionado?.size > MAX_ICON_SIZE_BYTES) {
      setSnackbar?.({
        children: `O ícone excede o tamanho máximo de ${MAX_ICON_SIZE_MB}MB.`,
        severity: "error",
      });
      return;
    }
    const dataUrl = await lerArquivoComoDataUrl(selecionado).catch(() => null);
    if (!dataUrl) {
      setSnackbar?.({
        children: "Não foi possível ler a imagem selecionada.",
        severity: "error",
      });
      return;
    }
    setFile(selecionado);
    setImageSrc(dataUrl);
    setCrop(CROP_INICIAL);
    setZoom(ZOOM_MIN);
    setCroppedAreaPixels(null);
  };

  // Enquanto o modal estiver aberto, o Ctrl+V tem prioridade máxima sobre qualquer outra área de
  // colar da página (ex: Anexos do circuito), independente de onde o foco do DOM esteja
  usePriorityPaste({
    active: open,
    priority: 0,
    onPasteFiles: (arquivos) => validarEDefinirArquivo(arquivos?.[0]),
  });

  const handleFileChange = (evento) => {
    const selecionado = evento?.target?.files?.[0];
    if (evento?.target) evento.target.value = "";
    validarEDefinirArquivo(selecionado);
  };

  const handleDrop = (evento) => {
    evento?.preventDefault?.();
    validarEDefinirArquivo(evento?.dataTransfer?.files?.[0]);
  };

  // Descarta a imagem carregada no cropper e volta para a área de seleção de arquivo
  const handleTrocarImagem = () => {
    setFile(null);
    setImageSrc(null);
    setCrop(CROP_INICIAL);
    setZoom(ZOOM_MIN);
    setCroppedAreaPixels(null);
  };

  const tituloValido = !!titulo?.trim?.();
  const podeSalvar = tituloValido && (mode !== "create" || !!file) && !saving;

  const handleSave = async () => {
    if (!podeSalvar) return;
    setSaving(true);
    try {
      // Recorta sempre que houver uma imagem carregada no cropper (nova ou a já existente
      // sendo reenquadrada); sem imagem carregada, edita só o título
      let finalFile = null;
      if (imageSrc && croppedAreaPixels) {
        const blob = await cropIconToCircle(imageSrc, croppedAreaPixels);
        if (blob) {
          finalFile = new File([blob], file?.name || "icone.png", {
            type: "image/png",
          });
        }
      }

      if (mode === "create") {
        await createCustomEquipmentType({
          titulo: titulo?.trim?.(),
          file: finalFile,
        });
      } else {
        await updateCustomEquipmentType({
          id: initialData?.id,
          titulo: titulo?.trim?.(),
          file: finalFile,
        });
      }
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Erro ao salvar equipamento personalizado:", error);
      setSnackbar?.({
        children: "Erro ao salvar equipamento personalizado.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { borderRadius: 16 } }}
    >
      <DialogTitle sx={modalStyles?.modalTitle}>
        <Typography variant="h6" fontWeight="bold">
          {mode === "create"
            ? "Novo equipamento personalizado"
            : "Editar equipamento personalizado"}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Fechar">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Título"
          fullWidth
          autoComplete="off"
          value={titulo}
          onChange={(evento) => setTitulo(evento?.target?.value || "")}
          sx={modalStyles?.titleField}
        />
        <Typography sx={modalStyles?.iconLabel}>Ícone</Typography>

        {imageSrc ? (
          <>
            <Box sx={modalStyles?.cropStage}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, areaEmPixels) =>
                  setCroppedAreaPixels(areaEmPixels)
                }
              />
            </Box>
            <Box sx={modalStyles?.zoomRow}>
              <ZoomOutOutlined fontSize="small" color="action" />
              <input
                type="range"
                value={zoom}
                min={ZOOM_MIN}
                max={ZOOM_MAX}
                step={0.1}
                aria-label="Zoom do ícone"
                onChange={(evento) =>
                  setZoom(Number(evento?.target?.value || ZOOM_MIN))
                }
                style={modalStyles?.zoomSlider}
              />
              <ZoomInOutlined fontSize="small" color="action" />
            </Box>
            <Button
              size="small"
              variant="text"
              startIcon={<ImageOutlined fontSize="small" />}
              onClick={handleTrocarImagem}
              sx={modalStyles?.trocarImagemButton}
            >
              Trocar imagem
            </Button>
          </>
        ) : (
          <FileDropzone
            files={[]}
            inputId="input-icone-equipamento-personalizado"
            accept={ACCEPT_ICON_TYPES}
            compact
            onDrop={handleDrop}
            onDragOver={(evento) => evento?.preventDefault?.()}
            onFileChange={handleFileChange}
            onRemoveFile={() => {}}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!podeSalvar}>
          {saving ? <CircularProgress size={22} /> : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
